/**
 * Scenario template helpers: kind, roles and slots. A scenario is a quiz doc with
 * `kind: 'scenario'` whose questions are answered by different people, one slot per step.
 * See docs/archive/QUIZ_SCENARIOS_PLAN.md.
 */

import type { Quiz, ScenarioRole, ScenarioSlot } from '../types';
import { newId } from './ids';

export const SCENARIO_ROLES: ScenarioRole[] = ['manager', 'warehouse', 'counter'];

export const SCENARIO_ROLE_LABELS: Record<ScenarioRole, string> = {
  manager: 'Manager',
  warehouse: 'Warehouse',
  counter: 'Counter',
};

export function isScenarioRole(value: unknown): value is ScenarioRole {
  return typeof value === 'string' && (SCENARIO_ROLES as string[]).includes(value);
}

/** Missing or unknown kind is a normal quiz. */
export function isScenario(quiz: Pick<Quiz, 'kind'> | null | undefined): boolean {
  return quiz?.kind === 'scenario';
}

/** Case-insensitive, trimmed: "Counter" and " counter " clash. */
export function slotLabelKey(label: string): string {
  return label.trim().toLowerCase();
}

/** "Counter", then "Counter 2", "Counter 3"... skipping labels already taken. */
export function suggestSlotLabel(role: ScenarioRole, slots: Pick<ScenarioSlot, 'label'>[]): string {
  const taken = new Set(slots.map((s) => slotLabelKey(s.label)));
  const base = SCENARIO_ROLE_LABELS[role];
  if (!taken.has(slotLabelKey(base))) return base;
  for (let n = 2; ; n++) {
    const label = `${base} ${n}`;
    if (!taken.has(slotLabelKey(label))) return label;
  }
}

export function newSlot(role: ScenarioRole, existing: Pick<ScenarioSlot, 'label'>[]): ScenarioSlot {
  return { id: newId(), role, label: suggestSlotLabel(role, existing) };
}

/** Error text for one slot label in the editor, or null. */
export function slotLabelError(slotId: string, label: string, slots: ScenarioSlot[]): string | null {
  if (!label.trim()) return 'Label is required';
  const key = slotLabelKey(label);
  if (slots.some((s) => s.id !== slotId && slotLabelKey(s.label) === key)) return 'Another slot has this label';
  return null;
}
